import { useState } from 'react';
import {
  ChatBubbleLeftRightIcon,
  EnvelopeIcon,
  UserGroupIcon,
  CheckCircleIcon,
  ArrowTrendingUpIcon,
  ExclamationTriangleIcon,
  ClockIcon,
  StarIcon,
  ChatBubbleBottomCenterTextIcon,
} from '@heroicons/react/24/outline';
import { StarIcon as StarSolid } from '@heroicons/react/24/solid';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';
import {
  useOverview,
  useConversationVolume,
  useMessageVolume,
  useConversationsByPriority,
  useStaffPerformance,
  useFeedbackSummary,
} from '../hooks/useAnalytics';

const PERIOD_OPTIONS = [
  { label: '7 days', value: 7 },
  { label: '30 days', value: 30 },
  { label: '90 days', value: 90 },
];

const PRIORITY_COLORS: Record<string, string> = {
  low: '#8A93AC',
  normal: '#3E8E24',
  high: '#FF6A3D',
  urgent: '#E5484D',
};

const STATE_COLORS: Record<string, string> = {
  bot_active: '#0E7CFF',
  awaiting_staff: '#FF6A3D',
  staff_active: '#8AD4FF',
  resolved: '#3E8E24',
  closed: '#8A93AC',
};

const STATE_LABELS: Record<string, string> = {
  bot_active: 'Bot Active',
  awaiting_staff: 'Awaiting Staff',
  staff_active: 'Staff Active',
  resolved: 'Resolved',
  closed: 'Closed',
};

export default function AnalyticsDashboard() {
  const [days, setDays] = useState(30);
  const { data: overview, isLoading: loadingOverview } = useOverview(days);
  const { data: convVolume = [] } = useConversationVolume(days);
  const { data: msgVolume = [] } = useMessageVolume(days);
  const { data: priorityData = [] } = useConversationsByPriority(days);
  const { data: staffPerf = [] } = useStaffPerformance(days);
  const { data: csatSummary } = useFeedbackSummary(days);

  // Convert state data for pie chart
  const stateData = overview
    ? Object.entries(overview.conversations.byState).map(([state, count]) => ({
        name: STATE_LABELS[state] || state,
        value: count,
        color: STATE_COLORS[state] || '#8A93AC',
      }))
    : [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-bold text-ink">Analytics</h1>
          <p className="text-sm text-ink-3">Overview of your support performance</p>
        </div>
        <div className="flex gap-1 rounded-pill border border-line p-1">
          {PERIOD_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              onClick={() => setDays(opt.value)}
              className={`rounded-pill px-3 py-1.5 text-sm font-medium transition-colors ${
                days === opt.value
                  ? 'bg-brand text-white'
                  : 'text-ink-2 hover:bg-black/[0.04]'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {loadingOverview ? (
        <div className="flex h-64 items-center justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-brand border-t-transparent" />
        </div>
      ) : overview ? (
        <>
          {/* Metric Cards */}
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            <MetricCard
              icon={<ChatBubbleLeftRightIcon className="h-5 w-5 text-brand" />}
              label="Conversations"
              value={overview.conversations.total}
            />
            <MetricCard
              icon={<EnvelopeIcon className="h-5 w-5 text-coral" />}
              label="Messages"
              value={overview.messages.total}
            />
            <MetricCard
              icon={<UserGroupIcon className="h-5 w-5 text-sky" />}
              label="New Customers"
              value={overview.customers.new}
            />
            <MetricCard
              icon={<CheckCircleIcon className="h-5 w-5 text-success" />}
              label="Resolution Rate"
              value={`${overview.conversations.resolutionRate}%`}
            />
          </div>

          {/* Secondary Metrics */}
          <div className="grid grid-cols-3 gap-4">
            <div className="rounded-card border border-line bg-surface p-4 shadow-s">
              <div className="flex items-center gap-2 text-sm text-ink-3">
                <ClockIcon className="h-4 w-4" />
                Avg Resolution Time
              </div>
              <p className="mt-1 font-display text-2xl font-bold text-ink">
                {overview.conversations.avgResolutionMinutes != null
                  ? `${overview.conversations.avgResolutionMinutes}m`
                  : '—'}
              </p>
            </div>
            <div className="rounded-card border border-line bg-surface p-4 shadow-s">
              <div className="flex items-center gap-2 text-sm text-ink-3">
                <ExclamationTriangleIcon className="h-4 w-4" />
                Escalation Rate
              </div>
              <p className="mt-1 font-display text-2xl font-bold text-ink">
                {overview.conversations.escalationRate}%
              </p>
            </div>
            <div className="rounded-card border border-line bg-surface p-4 shadow-s">
              <div className="flex items-center gap-2 text-sm text-ink-3">
                <ArrowTrendingUpIcon className="h-4 w-4" />
                Bot vs Staff Messages
              </div>
              <p className="mt-1 font-display text-2xl font-bold text-ink">
                {overview.messages.bySender.agent || 0} / {overview.messages.bySender.staff || 0}
              </p>
            </div>
          </div>

          {/* Customer Feedback (CSAT) — only shown when the store has any
              triggered prompts in the window. Stays empty (no row) for stores
              that haven't enabled CSAT yet so the dashboard doesn't get noisy. */}
          {csatSummary && csatSummary.totalTriggered > 0 && (
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
              <div className="rounded-card border border-line bg-surface p-4 shadow-s">
                <div className="flex items-center gap-2 text-sm text-ink-3">
                  <StarIcon className="h-4 w-4 text-warning" />
                  Avg CSAT
                </div>
                <div className="mt-2 flex items-baseline gap-2">
                  <p className="font-display text-3xl font-bold text-ink">
                    {csatSummary.averageScore != null ? csatSummary.averageScore.toFixed(2) : '—'}
                  </p>
                  {csatSummary.averageScore != null && (
                    <span className="flex items-center gap-0.5" aria-hidden>
                      {[1, 2, 3, 4, 5].map((n) =>
                        n <= Math.round(csatSummary.averageScore!) ? (
                          <StarSolid key={n} className="h-3.5 w-3.5 text-warning" />
                        ) : (
                          <StarIcon key={n} className="h-3.5 w-3.5 text-warning/30" />
                        )
                      )}
                    </span>
                  )}
                </div>
                <p className="mt-1 text-xs text-ink-3">
                  {csatSummary.totalResponded} response{csatSummary.totalResponded === 1 ? '' : 's'}
                </p>
              </div>

              <div className="rounded-card border border-line bg-surface p-4 shadow-s">
                <div className="flex items-center gap-2 text-sm text-ink-3">
                  <ChatBubbleBottomCenterTextIcon className="h-4 w-4 text-success" />
                  Response Rate
                </div>
                <p className="mt-2 font-display text-3xl font-bold text-ink">{csatSummary.responseRate}%</p>
                <p className="mt-1 text-xs text-ink-3">
                  {csatSummary.totalResponded} of {csatSummary.totalTriggered} surveys
                </p>
              </div>

              <div className="rounded-card border border-line bg-surface p-4 shadow-s">
                <div className="text-sm text-ink-3">Score Distribution</div>
                <div className="mt-2 space-y-1.5">
                  {[5, 4, 3, 2, 1].map((score) => {
                    const count = csatSummary.distribution[score] || 0;
                    const total = csatSummary.totalResponded || 1;
                    const pct = (count / total) * 100;
                    return (
                      <div key={score} className="flex items-center gap-2">
                        <span className="w-3 text-xs text-warning">{score}</span>
                        <div className="flex h-2 flex-1 overflow-hidden rounded-full bg-surface-2">
                          <div
                            className="bg-warning/80"
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                        <span className="w-6 text-right text-xs text-ink-2">{count}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* Charts Row 1 */}
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            {/* Conversation Volume */}
            <div className="rounded-card border border-line bg-surface p-4 shadow-s">
              <h3 className="mb-4 font-display text-sm font-semibold text-ink">Conversation Volume</h3>
              {convVolume.length > 0 ? (
                <ResponsiveContainer width="100%" height={250}>
                  <AreaChart data={convVolume}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(15,26,51,.08)" />
                    <XAxis
                      dataKey="date"
                      tick={{ fontSize: 11, fill: '#8A93AC' }}
                      axisLine={{ stroke: 'rgba(15,26,51,.10)' }}
                      tickLine={{ stroke: 'rgba(15,26,51,.10)' }}
                      tickFormatter={(v) => new Date(v).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    />
                    <YAxis
                      tick={{ fontSize: 11, fill: '#8A93AC' }}
                      axisLine={{ stroke: 'rgba(15,26,51,.10)' }}
                      tickLine={{ stroke: 'rgba(15,26,51,.10)' }}
                      allowDecimals={false}
                    />
                    <Tooltip
                      contentStyle={{ backgroundColor: '#FFFFFF', color: '#0F1A33', border: '1px solid rgba(15,26,51,.10)', borderRadius: 12, boxShadow: '0 6px 20px rgba(15,26,51,.10)' }}
                      labelStyle={{ color: '#0F1A33' }}
                      labelFormatter={(v) => new Date(v).toLocaleDateString('en-US', { month: 'long', day: 'numeric' })}
                    />
                    <Area type="monotone" dataKey="total" stroke="#0E7CFF" fill="rgba(14,124,255,.15)" name="Total" />
                    <Area type="monotone" dataKey="resolved" stroke="#3E8E24" fill="rgba(62,142,36,.15)" name="Resolved" />
                    <Area type="monotone" dataKey="escalated" stroke="#FF6A3D" fill="rgba(255,106,61,.15)" name="Escalated" />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <EmptyChart />
              )}
            </div>

            {/* Message Volume */}
            <div className="rounded-card border border-line bg-surface p-4 shadow-s">
              <h3 className="mb-4 font-display text-sm font-semibold text-ink">Message Volume</h3>
              {msgVolume.length > 0 ? (
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={msgVolume}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(15,26,51,.08)" />
                    <XAxis
                      dataKey="date"
                      tick={{ fontSize: 11, fill: '#8A93AC' }}
                      axisLine={{ stroke: 'rgba(15,26,51,.10)' }}
                      tickLine={{ stroke: 'rgba(15,26,51,.10)' }}
                      tickFormatter={(v) => new Date(v).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    />
                    <YAxis
                      tick={{ fontSize: 11, fill: '#8A93AC' }}
                      axisLine={{ stroke: 'rgba(15,26,51,.10)' }}
                      tickLine={{ stroke: 'rgba(15,26,51,.10)' }}
                      allowDecimals={false}
                    />
                    <Tooltip
                      contentStyle={{ backgroundColor: '#FFFFFF', color: '#0F1A33', border: '1px solid rgba(15,26,51,.10)', borderRadius: 12, boxShadow: '0 6px 20px rgba(15,26,51,.10)' }}
                      labelStyle={{ color: '#0F1A33' }}
                      cursor={{ fill: 'rgba(15,26,51,.04)' }}
                      labelFormatter={(v) => new Date(v).toLocaleDateString('en-US', { month: 'long', day: 'numeric' })}
                    />
                    <Bar dataKey="customer" stackId="a" fill="#0E7CFF" name="Customer" />
                    <Bar dataKey="agent" stackId="a" fill="#FF6A3D" name="AI Agent" />
                    <Bar dataKey="staff" stackId="a" fill="#3E8E24" name="Staff" />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <EmptyChart />
              )}
            </div>
          </div>

          {/* Charts Row 2 */}
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            {/* Conversations by State */}
            <div className="rounded-card border border-line bg-surface p-4 shadow-s">
              <h3 className="mb-4 font-display text-sm font-semibold text-ink">By State</h3>
              {stateData.length > 0 ? (
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie
                      data={stateData}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={80}
                      dataKey="value"
                      paddingAngle={2}
                    >
                      {stateData.map((entry, i) => (
                        <Cell key={i} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{ backgroundColor: '#FFFFFF', color: '#0F1A33', border: '1px solid rgba(15,26,51,.10)', borderRadius: 12, boxShadow: '0 6px 20px rgba(15,26,51,.10)' }}
                      labelStyle={{ color: '#0F1A33' }}
                    />
                    <Legend iconSize={8} wrapperStyle={{ fontSize: 11, color: '#55617F' }} />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <EmptyChart />
              )}
            </div>

            {/* Conversations by Priority */}
            <div className="rounded-card border border-line bg-surface p-4 shadow-s">
              <h3 className="mb-4 font-display text-sm font-semibold text-ink">By Priority</h3>
              {priorityData.length > 0 ? (
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={priorityData} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(15,26,51,.08)" />
                    <XAxis
                      type="number"
                      tick={{ fontSize: 11, fill: '#8A93AC' }}
                      axisLine={{ stroke: 'rgba(15,26,51,.10)' }}
                      tickLine={{ stroke: 'rgba(15,26,51,.10)' }}
                      allowDecimals={false}
                    />
                    <YAxis
                      type="category"
                      dataKey="priority"
                      tick={{ fontSize: 11, fill: '#8A93AC' }}
                      axisLine={{ stroke: 'rgba(15,26,51,.10)' }}
                      tickLine={{ stroke: 'rgba(15,26,51,.10)' }}
                      width={60}
                      tickFormatter={(v) => v.charAt(0).toUpperCase() + v.slice(1)}
                    />
                    <Tooltip
                      contentStyle={{ backgroundColor: '#FFFFFF', color: '#0F1A33', border: '1px solid rgba(15,26,51,.10)', borderRadius: 12, boxShadow: '0 6px 20px rgba(15,26,51,.10)' }}
                      labelStyle={{ color: '#0F1A33' }}
                      cursor={{ fill: 'rgba(15,26,51,.04)' }}
                    />
                    <Bar dataKey="count" name="Conversations">
                      {priorityData.map((entry, i) => (
                        <Cell key={i} fill={PRIORITY_COLORS[entry.priority] || '#8A93AC'} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <EmptyChart />
              )}
            </div>

            {/* Staff Performance */}
            <div className="rounded-card border border-line bg-surface p-4 shadow-s">
              <h3 className="mb-4 font-display text-sm font-semibold text-ink">Staff Performance</h3>
              {staffPerf.length > 0 ? (
                <div className="space-y-3">
                  {staffPerf.map((s) => (
                    <div key={s.staffId} className="flex items-center justify-between">
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium text-ink">{s.name}</p>
                        <p className="text-xs text-ink-3">
                          {s.conversations} conv · {s.resolved} resolved · {s.messages} msgs
                        </p>
                      </div>
                      <div className="ml-2 flex flex-col items-end gap-0.5">
                        {s.conversations > 0 && (
                          <span className="text-xs font-medium text-success">
                            {Math.round((s.resolved / s.conversations) * 100)}%
                          </span>
                        )}
                        {s.avgCsatScore != null && (
                          <span
                            className="flex items-center gap-0.5 text-xs font-medium text-warning"
                            title={`Average CSAT across ${s.csatResponded} response${s.csatResponded === 1 ? '' : 's'}`}
                          >
                            <StarSolid className="h-3 w-3 text-warning" />
                            {s.avgCsatScore.toFixed(1)}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="flex h-40 items-center justify-center text-sm text-ink-2">
                  No staff activity
                </p>
              )}
            </div>
          </div>
        </>
      ) : (
        <div className="flex h-64 items-center justify-center text-ink-3">
          Failed to load analytics data
        </div>
      )}
    </div>
  );
}

function MetricCard({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string | number;
}) {
  return (
    <div className="rounded-card border border-line bg-surface p-4 shadow-s">
      <div className="flex items-center gap-2 text-sm text-ink-3">
        {icon}
        {label}
      </div>
      <p className="mt-2 font-display text-3xl font-bold text-ink">{value}</p>
    </div>
  );
}

function EmptyChart() {
  return (
    <div className="flex h-[220px] items-center justify-center text-sm text-ink-2">
      No data for this period
    </div>
  );
}
